/**
 * Страница навыка (/skills/:skillId).
 *
 * Две карточки: установка навыка в агента (curl или ссылка на сырой файл) и
 * сам документ SKILL.md, отрендеренный в стилистике портала. Сырые файлы лежат
 * в /skills/<id>/ — их кладёт туда scripts/sync-skills.mjs, поэтому адрес
 * установки строится от origin текущего стенда, а не зашит в бандл.
 */
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../app/routes";
import { loadSkillMarkdown, skillById } from "../app/skills";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Card,
  CodeBlock,
  Link,
  Loader,
  Tabs,
  typographyClass,
} from "../ui-kit";

// react-markdown с зависимостями нужен только здесь — не тащим его в основной бандл.
const SkillMarkdown = lazy(() =>
  import("./SkillMarkdown").then((module) => ({ default: module.SkillMarkdown })),
);

type InstallMethod = "curl" | "manual";

const INSTALL_LABELS: Record<InstallMethod, string> = {
  curl: "curl",
  manual: "URL",
};

/** Команда/адрес установки для выбранного способа. */
function installSnippet(method: InstallMethod, id: string, rawUrl: string): { language: string; code: string } {
  switch (method) {
    case "curl":
      return {
        language: "bash",
        code: `mkdir -p ~/.claude/skills/${id} && \\
curl -fsSL ${rawUrl} \\
  -o ~/.claude/skills/${id}/SKILL.md`,
      };
    case "manual":
      return { language: "text", code: rawUrl };
  }
}

const SECTION_TEXT = { color: "var(--color-text-secondary-v2)", maxWidth: "68ch" } as const;

export function SkillPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { skillId = "" } = useParams();
  const skill = skillById(skillId);

  const [method, setMethod] = useState<InstallMethod>("curl");
  const [raw, setRaw] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!skill) return;
    let alive = true;
    setRaw(null);
    setFailed(false);
    loadSkillMarkdown(skill.id).then(
      (markdown) => alive && setRaw(markdown),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [skill]);

  if (!skill) {
    return (
      <Card>
        <AlertBanner tone="error" title={t("skill.notFound")} />
        <Link onClick={() => navigate(ROUTES.skills)}>{t("skill.backToList")}</Link>
      </Card>
    );
  }

  const rawPath = `/skills/${skill.id}/SKILL.md`;
  const rawUrl = `${window.location.origin}${rawPath}`;

  return (
    <>
      <Card>
        <Link onClick={() => navigate(ROUTES.skills)}>{t("skill.backToList")}</Link>
        <h1 className={typographyClass("header-24-semibold")}>{skill.name}</h1>
        <p className={typographyClass("text-14-medium")} style={SECTION_TEXT}>
          {skill.description}
        </p>

        <h2 className={typographyClass("header-16-semibold")}>{t("skill.install.title")}</h2>
        <p className={typographyClass("text-14-medium")} style={SECTION_TEXT}>
          {t("skill.install.mcp")}{" "}
          <Link onClick={() => navigate(ROUTES.mcp)}>{t("skill.install.mcpLink")} →</Link>
        </p>
        <p className={typographyClass("text-14-medium")} style={SECTION_TEXT}>
          {t("skill.install.text")}
        </p>

        <Tabs<InstallMethod>
          value={method}
          onChange={setMethod}
          items={(Object.keys(INSTALL_LABELS) as InstallMethod[]).map((key) => ({
            value: key,
            label: INSTALL_LABELS[key],
          }))}
        />
        <CodeBlock {...installSnippet(method, skill.id, rawUrl)} copyLabel={t("action.copy")} />
        <Link href={rawPath} target="_blank">
          {t("skill.install.open")} →
        </Link>
      </Card>

      <Card>
        <h2 className={typographyClass("header-16-semibold")}>{t("skill.doc.title")}</h2>
        {raw === null && !failed && <Loader label={t("status.loading")} />}
        {failed && <AlertBanner tone="error" title={t("status.error")}>{t("skill.doc.error")}</AlertBanner>}
        {raw !== null && (
          <Suspense fallback={<Loader label={t("status.loading")} />}>
            <SkillMarkdown source={raw} />
          </Suspense>
        )}
      </Card>
    </>
  );
}
